/**
 * useDeduplicatedEffect - React effect hook that prevents duplicate executions
 * during React 18 Strict Mode double-mounting.
 *
 * Problem: React Strict Mode intentionally mounts components twice in development
 * to help detect side effects. This causes API calls to fire twice.
 *
 * Solution: Track effect execution with a ref that persists across remounts
 * within the same Strict Mode cycle, only re-running when dependencies actually change.
 */

import { useEffect, useRef } from "react";

/**
 * Helper to check if two dependency arrays are equal
 */
function areDepsEqual(
  deps1: React.DependencyList,
  deps2: React.DependencyList,
): boolean {
  if (deps1.length !== deps2.length) return false;
  return deps1.every((dep, index) => Object.is(dep, deps2[index]));
}

/**
 * useDeduplicatedEffect - Prevents duplicate effect executions in Strict Mode
 *
 * @param effect - The effect function to run (should return cleanup function or void)
 * @param deps - Dependency array (same as useEffect)
 *
 * @example
 * ```tsx
 * useDeduplicatedEffect(() => {
 *   fetchData();
 * }, [userId]);
 * ```
 */
export function useDeduplicatedEffect(
  effect: () => void | (() => void),
  deps: React.DependencyList,
): void {
  const hasExecutedRef = useRef(false);
  const prevDepsRef = useRef<React.DependencyList>(deps);
  const cleanupRef = useRef<void | (() => void)>();

  useEffect(() => {
    // Check if dependencies actually changed (not just remount)
    const depsChanged = !areDepsEqual(prevDepsRef.current, deps);

    // Execute effect only if:
    // 1. First time running (hasExecutedRef.current === false), OR
    // 2. Dependencies actually changed
    if (!hasExecutedRef.current || depsChanged) {
      // Run cleanup from previous execution if it exists
      if (cleanupRef.current) {
        cleanupRef.current();
      }

      // Mark as executed and update deps
      hasExecutedRef.current = true;
      prevDepsRef.current = deps;

      // Execute the effect and store cleanup function
      cleanupRef.current = effect();
    }

    // Cleanup on unmount (but don't reset hasExecutedRef for Strict Mode tolerance)
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = undefined;
      }
    };
  }, deps);
}
